// frontend/src/services/admin.service.ts

import { authService } from './auth.service';
import {
  AdminUserSummary,
  AdminUserStats,
  AdminCourse,
  AdminLesson,
  AdminProject,
  AiUsageSummary,
  AdminOverview,
  AdminLessonTopic,
  ContactMessage,
} from '../types/admin.types';
import { StudentListItem, StudentAnalytics, TopicScore } from '../types/studentAnalytics.types';

const API_BASE = (import.meta.env.PROD && (import.meta.env.VITE_API_BASE_URL === 'http://localhost:8080' || !import.meta.env.VITE_API_BASE_URL)) ? '' : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080');

class AdminApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AdminApiError';
  }
}

export async function authedFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await authService.getAccessToken();
  if (!token) throw new AdminApiError('Oturum bulunamadı, lütfen giriş yapın.');

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new AdminApiError(data?.error?.message || `İstek başarısız (HTTP ${res.status})`);
  return data as T;
}

export const adminService = {
  // --- Users ---
  async listUsers(filters: { role?: string; search?: string; status?: string } = {}): Promise<AdminUserSummary[]> {
    const params = new URLSearchParams(filters as Record<string, string>);
    const data = await authedFetch<{ users: AdminUserSummary[] }>(`/api/admin/users?${params}`);
    return data.users;
  },

  async getUserStats(): Promise<AdminUserStats> {
    return authedFetch<AdminUserStats>('/api/admin/users/stats');
  },

  async banUser(userId: string, reason: string): Promise<void> {
    await authedFetch(`/api/admin/users/${userId}/ban`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  async unbanUser(userId: string): Promise<void> {
    await authedFetch(`/api/admin/users/${userId}/unban`, { method: 'POST' });
  },

  // --- Content: Courses ---
  async listCourses(): Promise<AdminCourse[]> {
    const data = await authedFetch<{ courses: AdminCourse[] }>('/api/admin/content/courses');
    return data.courses;
  },

  async createCourse(input: {
    title: string;
    description: string;
    category: string;
    difficulty: string;
    is_published?: boolean;
    order_index?: number;
    age_category?: string | null;
  }): Promise<{ id: string }> {
    return authedFetch('/api/admin/content/courses', { method: 'POST', body: JSON.stringify(input) });
  },

  async updateCourse(courseId: string, input: Partial<AdminCourse>): Promise<void> {
    await authedFetch(`/api/admin/content/courses/${courseId}`, { method: 'PATCH', body: JSON.stringify(input) });
  },

  async deleteCourse(courseId: string): Promise<void> {
    await authedFetch(`/api/admin/content/courses/${courseId}`, { method: 'DELETE' });
  },

  // --- Content: Lessons ---
  async listLessons(courseId: string): Promise<AdminLesson[]> {
    const data = await authedFetch<{ lessons: AdminLesson[] }>(`/api/admin/content/courses/${courseId}/lessons`);
    return data.lessons;
  },

  async createLesson(input: {
    course_id: string;
    title: string;
    topic?: string;
    content: string;
    video_url?: string;
    order_index?: number;
    xp_reward?: number;
  }): Promise<{ id: string }> {
    return authedFetch('/api/admin/content/lessons', { method: 'POST', body: JSON.stringify(input) });
  },

  async updateLesson(lessonId: string, input: Partial<AdminLesson>): Promise<void> {
    await authedFetch(`/api/admin/content/lessons/${lessonId}`, { method: 'PATCH', body: JSON.stringify(input) });
  },

  async deleteLesson(lessonId: string): Promise<void> {
    await authedFetch(`/api/admin/content/lessons/${lessonId}`, { method: 'DELETE' });
  },

  // --- Content: Lesson Topics ---
  async listLessonTopics(lessonId: string): Promise<AdminLessonTopic[]> {
    const data = await authedFetch<{ topics: AdminLessonTopic[] }>(`/api/admin/content/lessons/${lessonId}/topics`);
    return data.topics;
  },

  async createLessonTopic(lessonId: string, input: Omit<AdminLessonTopic, 'id' | 'lesson_id' | 'created_at'>): Promise<{ id: string }> {
    return authedFetch(`/api/admin/content/lessons/${lessonId}/topics`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  async updateLessonTopic(topicId: string, input: Partial<AdminLessonTopic>): Promise<void> {
    await authedFetch(`/api/admin/content/lessons/topics/${topicId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  },

  async deleteLessonTopic(topicId: string): Promise<void> {
    await authedFetch(`/api/admin/content/lessons/topics/${topicId}`, {
      method: 'DELETE',
    });
  },

  // --- Content: Projects ---
  async listProjects(): Promise<AdminProject[]> {
    const data = await authedFetch<{ projects: AdminProject[] }>('/api/admin/content/projects');
    return data.projects;
  },

  async createProject(input: Partial<AdminProject>): Promise<{ id: string }> {
    return authedFetch('/api/admin/content/projects', { method: 'POST', body: JSON.stringify(input) });
  },

  async updateProject(projectId: string, input: Partial<AdminProject>): Promise<void> {
    await authedFetch(`/api/admin/content/projects/${projectId}`, { method: 'PATCH', body: JSON.stringify(input) });
  },

  async deleteProject(projectId: string): Promise<void> {
    await authedFetch(`/api/admin/content/projects/${projectId}`, { method: 'DELETE' });
  },

  // --- Content: Quizzes ---
  async createQuiz(input: {
    course_id?: string | null;
    title: string;
    difficulty: string;
    questions: {
      type: string;
      question: string;
      options?: string[];
      correctAnswer: string;
      explanation?: string;
    }[];
  }): Promise<{ id: string }> {
    return authedFetch('/api/admin/content/quizzes', { method: 'POST', body: JSON.stringify(input) });
  },

  // --- Analytics ---
  async getAiUsage(days = 30): Promise<AiUsageSummary> {
    return authedFetch<AiUsageSummary>(`/api/admin/analytics/ai-usage?days=${days}`);
  },

  async getOverview(): Promise<AdminOverview> {
    return authedFetch<AdminOverview>('/api/admin/analytics/overview');
  },

  // --- Student Analytics ---
  async listStudentsForAnalytics(): Promise<StudentListItem[]> {
    const data = await authedFetch<{ students: StudentListItem[] }>('/api/admin/student-analytics');
    return data.students;
  },

  async getStudentAnalytics(studentId: string): Promise<StudentAnalytics> {
    return authedFetch<StudentAnalytics>(`/api/admin/student-analytics/${studentId}`);
  },

  async getStudentAiRecommendation(
    studentId: string,
    studentName: string,
    strengths: TopicScore[],
    weaknesses: TopicScore[]
  ): Promise<string> {
    const data = await authedFetch<{ recommendation: string }>(
      `/api/admin/student-analytics/${studentId}/ai-recommendation`,
      { method: 'POST', body: JSON.stringify({ studentName, strengths, weaknesses }) }
    );
    return data.recommendation;
  },

  // --- Contact Messages ---
  async getContactMessages(): Promise<ContactMessage[]> {
    const data = await authedFetch<{ messages: ContactMessage[] }>('/api/admin/messages');
    return data.messages;
  },

  async deleteContactMessage(messageId: string): Promise<void> {
    await authedFetch(`/api/admin/messages/${messageId}`, { method: 'DELETE' });
  },
};

export { AdminApiError };

// Coin Shop CRUD (backend üzerinden - service_role kullanır)
export const coinShopAdminService = {
  async getItems(): Promise<any[]> {
    const data = await authedFetch<{ items: any[] }>('/api/admin/content/coin-shop');
    return data.items;
  },
  async addItem(item: { name: string; description: string; category: string; price_coins: number; image_emoji: string; image_url?: string | null }): Promise<{ id: string }> {
    return authedFetch('/api/admin/content/coin-shop', { method: 'POST', body: JSON.stringify(item) });
  },
  async updateItem(itemId: string, patch: Record<string, any>): Promise<void> {
    await authedFetch(`/api/admin/content/coin-shop/${itemId}`, { method: 'PATCH', body: JSON.stringify(patch) });
  },
  async deleteItem(itemId: string): Promise<void> {
    await authedFetch(`/api/admin/content/coin-shop/${itemId}`, { method: 'DELETE' });
  },
  async listOrders(): Promise<any[]> {
    const data = await authedFetch<{ orders: any[] }>('/api/admin/content/coin-shop/orders');
    return data.orders;
  },
  async updateOrder(purchaseId: string, input: { status?: string; notes?: string }): Promise<void> {
    await authedFetch(`/api/admin/content/coin-shop/orders/${purchaseId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  },
};

